import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { Quotation } from '../data/dummyData';

interface QuotationDetailProps {
  quotation: Quotation;
  onBack: () => void;
  onNavigate?: (view: 'dashboard' | 'quotations' | 'analytics' | 'settings' | 'ai-assistant' | 'user-management') => void;
}

const QuotationDetail: React.FC<QuotationDetailProps> = ({ quotation, onBack, onNavigate }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Acknowledged': return 'bg-yellow-100 text-yellow-800';
      case 'Quoted': return 'bg-purple-100 text-purple-800';
      case 'Won': return 'bg-green-100 text-green-800';
      case 'Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'Email': return '📧';
      case 'WhatsApp': return '💬';
      case 'Phone': return '📞';
      case 'Web': return '🌐';
      default: return '📄';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'ai-analysis', label: 'AI Analysis', icon: '🤖' },
    { id: 'attachments', label: 'Attachments', icon: '📎' },
    { id: 'history', label: 'Email Thread', icon: '📧' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar currentView="detail" onNavigate={(view) => {
        if (view === 'dashboard') {
          onBack();
        } else if (onNavigate) {
          onNavigate(view);
        }
      }} />

      {/* Main Content */}
      <div className="flex-1 ml-64">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{quotation.subject}</h1>
                <p className="text-gray-600 mt-1">
                  {quotation.customerName} • {quotation.company} • {quotation.serviceType}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(quotation.status)}`}>
                  {quotation.status}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(quotation.priority)}`}>
                  {quotation.priority}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Tabs */}
          <div className="card mb-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Quotation Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customer:</span>
                      <span className="font-medium">{quotation.customerName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Company:</span>
                      <span className="font-medium">{quotation.company}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service Type:</span>
                      <span className="font-medium">{quotation.serviceType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Origin:</span>
                      <span className="font-medium">{quotation.origin}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Destination:</span>
                      <span className="font-medium">{quotation.destination}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Weight:</span>
                      <span className="font-medium">{quotation.weight}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dimensions:</span>
                      <span className="font-medium">{quotation.dimensions}</span>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📦 Cargo Information</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-gray-600">Description:</span>
                      <p className="font-medium mt-1">{quotation.cargoDescription}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Special Requirements:</span>
                      <p className="font-medium mt-1">{quotation.specialRequirements}</p>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Channel:</span>
                      <span className="font-medium flex items-center">
                        <span className="mr-2">{getChannelIcon(quotation.channel)}</span>
                        {quotation.channel}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Assigned To:</span>
                      <span className="font-medium">{quotation.assignedTo}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Received:</span>
                      <span className="font-medium">{new Date(quotation.received).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Summary */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🤖 AI Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-600">Lead Name:</span>
                      <p className="text-gray-900 font-semibold">{quotation.aiSummary.leadName}</p>
                    </div>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-600">Requirements:</span>
                      <p className="text-gray-900">{quotation.aiSummary.requirements}</p>
                    </div>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-600">Urgency:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(quotation.aiSummary.urgency)}`}>
                        {quotation.aiSummary.urgency}
                      </span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Extracted Fields</h4>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Product:</span>
                        <p className="text-gray-900">{quotation.aiSummary.extractedFields.product}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Quantity:</span>
                        <p className="text-gray-900">{quotation.aiSummary.extractedFields.quantity}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Incoterms:</span>
                        <p className="text-gray-900">{quotation.aiSummary.extractedFields.incoterms}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Timeline:</span>
                        <p className="text-gray-900">{quotation.aiSummary.extractedFields.timeline}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai-analysis' && (
            <div className="space-y-6">
              {/* AI Summary */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🤖 AI Analysis Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-600">Lead Name:</span>
                      <p className="text-gray-900 font-semibold">{quotation.aiSummary.leadName}</p>
                    </div>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-600">Requirements:</span>
                      <p className="text-gray-900">{quotation.aiSummary.requirements}</p>
                    </div>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-600">Urgency:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(quotation.aiSummary.urgency)}`}>
                        {quotation.aiSummary.urgency}
                      </span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Extracted Fields</h4>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Product:</span>
                        <p className="text-gray-900">{quotation.aiSummary.extractedFields.product}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Quantity:</span>
                        <p className="text-gray-900">{quotation.aiSummary.extractedFields.quantity}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Incoterms:</span>
                        <p className="text-gray-900">{quotation.aiSummary.extractedFields.incoterms}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Timeline:</span>
                        <p className="text-gray-900">{quotation.aiSummary.extractedFields.timeline}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Thread Analysis */}
              {quotation.emailThread && quotation.emailThread.length > 0 && (
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📧 Email Thread Analysis</h3>
                  <div className="space-y-6">
                    {quotation.emailThread.map((email, index) => (
                      <div key={email.id} className={`border-l-4 pl-4 py-4 ${
                        email.type === 'incoming' ? 'border-blue-500 bg-blue-50' : 
                        email.type === 'outgoing' ? 'border-green-500 bg-green-50' : 
                        'border-purple-500 bg-purple-50'
                      } rounded-r-lg`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              email.type === 'incoming' ? 'bg-blue-100' : 
                              email.type === 'outgoing' ? 'bg-green-100' : 
                              'bg-purple-100'
                            }`}>
                              {email.type === 'incoming' ? '📥' : email.type === 'outgoing' ? '📤' : '💬'}
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{email.subject}</h4>
                              <p className="text-sm text-gray-600">
                                {email.type === 'incoming' ? 'From' : 'To'}: {email.sender}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-sm text-gray-500">
                              {new Date(email.timestamp).toLocaleString()}
                            </span>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                email.priority === 'High' ? 'bg-red-100 text-red-800' :
                                email.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {email.priority}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                email.status === 'read' ? 'bg-gray-100 text-gray-800' :
                                email.status === 'unread' ? 'bg-blue-100 text-blue-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {email.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* AI Analysis for this email */}
                        {email.aiExtractedInfo && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">🤖 AI Key Points:</h5>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {email.aiExtractedInfo.keyPoints.map((point, pointIndex) => (
                                  <li key={pointIndex} className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>{point}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">📋 Action Items:</h5>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {email.aiExtractedInfo.actionItems.map((item, itemIndex) => (
                                  <li key={itemIndex} className="flex items-start">
                                    <span className="mr-2">✓</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        {/* Email Content Summary */}
                        <div className="mb-3">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">📝 Content Summary:</h5>
                          <p className="text-sm text-gray-700 bg-white p-3 rounded border">
                            {email.content.length > 200 ? 
                              `${email.content.substring(0, 200)}...` : 
                              email.content
                            }
                          </p>
                        </div>

                        {/* AI Sentiment and Urgency Analysis */}
                        {email.aiExtractedInfo && (
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Sentiment:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                email.aiExtractedInfo.sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                                email.aiExtractedInfo.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {email.aiExtractedInfo.sentiment}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">AI Urgency:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                email.aiExtractedInfo.urgency === 'High' ? 'bg-red-100 text-red-800' :
                                email.aiExtractedInfo.urgency === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {email.aiExtractedInfo.urgency}
                              </span>
                            </div>
                            {email.tags && email.tags.length > 0 && (
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">Tags:</span>
                                <div className="flex flex-wrap gap-1">
                                  {email.tags.slice(0, 3).map((tag, tagIndex) => (
                                    <span key={tagIndex} className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                                      #{tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Communication Insights */}
              {quotation.emailThread && quotation.emailThread.length > 0 && (
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Communication Insights</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-600">Total Emails</p>
                          <p className="text-2xl font-bold text-blue-900">{quotation.emailThread.length}</p>
                        </div>
                        <div className="text-2xl">📧</div>
                      </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-600">Response Time</p>
                          <p className="text-2xl font-bold text-green-900">
                            {quotation.emailThread.length > 1 ? 
                              `${Math.round((new Date(quotation.emailThread[1].timestamp).getTime() - new Date(quotation.emailThread[0].timestamp).getTime()) / (1000 * 60 * 60))}h` : 
                              'N/A'
                            }
                          </p>
                        </div>
                        <div className="text-2xl">⏱️</div>
                      </div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-purple-600">Avg Sentiment</p>
                          <p className="text-2xl font-bold text-purple-900">
                            {quotation.emailThread.filter(e => e.aiExtractedInfo?.sentiment === 'positive').length > 
                             quotation.emailThread.filter(e => e.aiExtractedInfo?.sentiment === 'negative').length ? 
                             '😊' : '😐'}
                          </p>
                        </div>
                        <div className="text-2xl">🤖</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'attachments' && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📎 Attachments</h3>
              {quotation.attachments && quotation.attachments.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {quotation.attachments.map((attachment, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">
                          {attachment.endsWith('.pdf') ? '📄' : '🖼️'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{attachment}</p>
                          <p className="text-sm text-gray-500">
                            {attachment.endsWith('.pdf') ? 'PDF Document' : 'Image File'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📎</div>
                  <p className="text-gray-500">No attachments available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📧 Email Thread Timeline</h3>
              {quotation.emailThread && quotation.emailThread.length > 0 ? (
                <div className="space-y-6">
                  {quotation.emailThread.map((email, index) => (
                    <div key={email.id} className={`border-l-4 pl-4 py-4 ${
                      email.type === 'incoming' ? 'border-blue-500 bg-blue-50' :
                      email.type === 'outgoing' ? 'border-green-500 bg-green-50' :
                      'border-purple-500 bg-purple-50'
                    } rounded-r-lg`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            email.type === 'incoming' ? 'bg-blue-100' :
                            email.type === 'outgoing' ? 'bg-green-100' :
                            'bg-purple-100'
                          }`}>
                            {email.type === 'incoming' ? '📥' : email.type === 'outgoing' ? '📤' : '💬'}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{email.subject}</h4>
                            <p className="text-sm text-gray-600">
                              {email.type === 'incoming' ? 'From' : 'To'}: {email.sender}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-gray-500">
                            {new Date(email.timestamp).toLocaleString()}
                          </span>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              email.priority === 'High' ? 'bg-red-100 text-red-800' :
                              email.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {email.priority}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              email.status === 'read' ? 'bg-gray-100 text-gray-800' :
                              email.status === 'unread' ? 'bg-blue-100 text-blue-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {email.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm text-gray-700 whitespace-pre-line">{email.content}</p>
                      </div>

                      {email.attachments && email.attachments.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-600 mb-2">Attachments:</p>
                          <div className="flex flex-wrap gap-2">
                            {email.attachments.map((attachment, attIndex) => (
                              <span key={attIndex} className="px-2 py-1 bg-gray-200 rounded text-xs">
                                📎 {attachment}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {email.tags && email.tags.length > 0 && (
                        <div className="mb-3">
                          <div className="flex flex-wrap gap-1">
                            {email.tags.map((tag, tagIndex) => (
                              <span key={tagIndex} className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {email.aiExtractedInfo && (
                        <div className="border-t pt-3 mt-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">🤖 AI Key Points:</h5>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {email.aiExtractedInfo.keyPoints.map((point, pointIndex) => (
                                  <li key={pointIndex} className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>{point}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">📋 Action Items:</h5>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {email.aiExtractedInfo.actionItems.map((item, itemIndex) => (
                                  <li key={itemIndex} className="flex items-start">
                                    <span className="mr-2">✓</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Sentiment:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                email.aiExtractedInfo.sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                                email.aiExtractedInfo.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {email.aiExtractedInfo.sentiment}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Urgency:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                email.aiExtractedInfo.urgency === 'High' ? 'bg-red-100 text-red-800' :
                                email.aiExtractedInfo.urgency === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {email.aiExtractedInfo.urgency}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📧</div>
                  <p className="text-gray-500">No email thread available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotationDetail;